// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/EnterpriseImpact.test.tsx
import { CaretDown, CaretRight, Database } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MetricBlockCondensed } from "./canvas/blocks/MetricBlockCondensed";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
  chatAtomAgentInstanceId,
} from "../agentComponents/chatAtomRegistry";

export type EnterpriseImpactMetric = {
  id: string;
  label: string;
  value: string;
};

export type EnterpriseImpactProps = {
  metrics: EnterpriseImpactMetric[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  testIdPrefix?: string;
};

/**
 * Collapsible greeting-stat cluster. Figma EnterpriseImpact 49613:438 —
 * AgentSteps header only (no panel fill/stroke) + MetricBlockCondensed trio.
 */
export function EnterpriseImpact({
  metrics,
  open: openProp,
  onOpenChange,
  testIdPrefix = "enterprise-impact",
}: EnterpriseImpactProps) {
  const { t } = useTranslation("ui");
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp != null;
  const expanded = isControlled ? openProp : uncontrolledOpen;

  const setExpanded = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.enterpriseImpact)}
      data-testid={testIdPrefix}
      // why: Figma 49613:438 has no muted panel chrome — header + chips only.
      className="flex flex-col gap-2 bg-transparent"
    >
      <button
        type="button"
        data-testid={`${testIdPrefix}-toggle`}
        aria-expanded={expanded}
        aria-label={
          expanded
            ? t("enterpriseImpact.collapseAriaLabel")
            : t("enterpriseImpact.expandAriaLabel")
        }
        onClick={() => setExpanded(!expanded)}
        // why: header label must not sit on muted gray-2 (AgentSteps toggle chrome).
        className="flex w-full items-center justify-between bg-transparent text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7"
      >
        <span className="flex items-center gap-2.5 bg-transparent">
          <Database
            size={16}
            className="shrink-0 text-puntt-neutral-gray-11"
            aria-hidden
          />
          <Text size="1" className="bg-transparent text-puntt-neutral-gray-11">
            {t("enterpriseImpact.title")}
          </Text>
        </span>
        {expanded ? (
          <CaretDown
            size={16}
            aria-hidden
            className="shrink-0 text-puntt-neutral-gray-11"
          />
        ) : (
          <CaretRight
            size={16}
            aria-hidden
            className="shrink-0 text-puntt-neutral-gray-11"
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`${testIdPrefix}-items`}
          className="grid grid-cols-1 gap-2.5 sm:grid-cols-3"
        >
          {metrics.map((metric) => (
            <MetricBlockCondensed
              key={metric.id}
              data-testid={`${testIdPrefix}-metric-${metric.id}`}
              agentComponentId={chatAtomAgentInstanceId(
                CHAT_ATOM_AGENT_COMPONENT_IDS.metricBlock,
                metric.id,
              )}
              label={metric.label}
              value={metric.value}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
